import { EyeIcon, EyeSlashIcon } from "@phosphor-icons/react";
import { forwardRef, useId, useState, type InputHTMLAttributes } from "react";

import { cn } from "../../lib/cn";

type TextFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, "id"> & {
  label: string;
  /** Guidance shown under the label, linked to the field. */
  hint?: string;
  /** The inline validation message; marks the field invalid. */
  error?: string;
};

/**
 * A labelled field in the filled style (P5-010). The boundary stays in the
 * strong border token: WCAG 1.4.11 needs a field's edge at 3:1 against its
 * panel, which a fill alone does not give (ADR 0027: accessibility wins).
 * A password field has a show/hide button (Musweu, 12 October 2026), so
 * people can check what they typed; it starts hidden.
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ className, error, hint, label, type, ...props }, ref) => {
    const id = useId();
    const [shown, setShown] = useState(false);
    const isPassword = type === "password";
    const hintId = hint ? `${id}-hint` : undefined;
    const errorId = error ? `${id}-error` : undefined;
    return (
      <div className={className}>
        <label className="block text-sm font-semibold" htmlFor={id}>
          {label}
        </label>
        {hint ? (
          <p className="mt-1 text-sm leading-6 text-text-muted" id={hintId}>
            {hint}
          </p>
        ) : null}
        <div className="relative mt-2">
          <input
            aria-describedby={[hintId, errorId].filter(Boolean).join(" ") || undefined}
            aria-invalid={error ? true : undefined}
            className={cn(
              "block min-h-12 w-full rounded-control border border-border-strong bg-surface-muted px-4 text-base text-text transition-[background-color,border-color] duration-150 hover:bg-surface-raised focus:bg-surface-raised focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus aria-[invalid=true]:border-danger",
              isPassword && "pr-14",
            )}
            id={id}
            ref={ref}
            type={isPassword && shown ? "text" : type}
            {...props}
          />
          {isPassword ? (
            <button
              aria-controls={id}
              aria-label={shown ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
              aria-pressed={shown}
              className="absolute inset-y-0 right-1 my-auto flex size-11 items-center justify-center rounded-control text-text-muted hover:text-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
              onClick={() => setShown((value) => !value)}
              type="button"
            >
              {shown ? <EyeSlashIcon aria-hidden size={22} /> : <EyeIcon aria-hidden size={22} />}
            </button>
          ) : null}
        </div>
        {error ? (
          <p className="mt-2 text-sm font-medium text-danger-strong" id={errorId}>
            {error}
          </p>
        ) : null}
      </div>
    );
  },
);

TextField.displayName = "TextField";
